import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Calendar,
  CheckCircle2,
  ChevronRight,
  FileText,
  MessageSquare,
  RefreshCw,
  Sparkles,
  Stethoscope,
  Upload,
  XCircle,
} from "lucide-react";
import API, { getApiBaseUrl } from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { getSocket } from "../../Api/socket";
import { tokenManager } from "../../Api/tokenManager";
import { SkeletonBlock, SkeletonText } from "../Skeleton/Skeleton";
import { getPatientNotificationPath } from "../../utils/notificationRoutes";
import { isNotificationUnread } from "../../utils/notificationUnread";
import "./PatientNotificationBell.scss";

/** Resolves patient JWT from role storage, legacy keys, or Redux (avoids hidden bell when only one source is set). */
export function getPatientAccessToken(reduxToken) {
  const fromRole = tokenManager.getTokenForRole("patient");
  if (fromRole) return fromRole;
  const legacy = tokenManager.getToken?.();
  if (legacy) return legacy;
  if (reduxToken) return reduxToken;
  try {
    return localStorage.getItem("patientToken") || localStorage.getItem("token") || null;
  } catch {
    return null;
  }
}

export const VIEWED_STORAGE_KEY = "healbid_patient_notifications_viewed_at";

/**
 * Axios full response OR unwrapped JSON body — normalize to a notification array.
 * Never returns undefined (callers rely on an array).
 */
export function normalizeNotificationListFromResponse(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const body = res?.config != null && res.data !== undefined ? res.data : res;
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  if (Array.isArray(body.notifications)) return body.notifications;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.results)) return body.results;
  if (Array.isArray(body.records)) return body.records;
  return [];
}

/** Mirrors GET /app/notifications prescription_rejected rows so the bell matches Documents even if the API list lags. */
export function rejectedPrescriptionsToNotifications(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((row) => String(row?.status || "").toLowerCase() === "rejected")
    .map((row) => {
      const prescriptionId = String(row._id || row.id || "").trim();
      const originalName = String(row.originalName || "").trim() || "your file";
      const documentType = String(row.type || "prescription").toLowerCase();
      const typePhrase =
        documentType === "report"
          ? "medical report"
          : documentType === "insurance"
            ? "insurance document"
            : "prescription";
      const matchRequestIdStr = row.matchRequestId ? String(row.matchRequestId).trim() : "";
      const rejectionReason = String(row.rejectionReason || "").trim();
      const bodyParts = [
        matchRequestIdStr
          ? `Please upload a new version of "${originalName}" (${typePhrase}) for your care request.`
          : `Please upload a new version of "${originalName}" (${typePhrase}).`,
        rejectionReason ? `Reason: ${rejectionReason}` : "",
        "Only this file needs to be replaced; your other uploads are unchanged.",
      ];
      const body = bodyParts.filter(Boolean).join(" ");
      const at = row.updatedAt || row.createdAt || new Date().toISOString();
      return {
        type: "prescription_rejected",
        title: "Document not approved — re-upload needed",
        body,
        prescriptionId,
        originalName,
        matchRequestId: matchRequestIdStr || undefined,
        rejectionReason: rejectionReason || undefined,
        createdAt: at,
        updatedAt: at,
        read: false,
      };
    })
    .filter((n) => n.prescriptionId);
}

export function mergePrescriptionRejectedNotifications(apiList, rxItems) {
  const rxNs = rejectedPrescriptionsToNotifications(rxItems);
  const seen = new Set(
    (apiList || [])
      .filter(
        (n) => String(n?.type || "").toLowerCase() === "prescription_rejected" && n.prescriptionId
      )
      .map((n) => String(n.prescriptionId))
  );
  const extra = rxNs.filter((n) => !seen.has(n.prescriptionId));
  return [...extra, ...(apiList || [])];
}

/** Prefer conversation, then appointment / match / bid hub. */
function patientNotificationProviderHref(n) {
  return getPatientNotificationPath(n);
}

export function PatientNotificationBody({ notification: n, onProviderNavigate }) {
  const navigate = useNavigate();
  const body = String(n?.body ?? "");
  const name = String(n?.providerName ?? "").trim();
  const to = patientNotificationProviderHref(n);
  if (!to || !name || !body.includes(name)) {
    return body || "—";
  }
  const i = body.indexOf(name);
  const before = body.slice(0, i);
  const after = body.slice(i + name.length);
  return (
    <>
      {before}
      <span
        className="patient_notification_item_body__providerLink"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            onProviderNavigate?.();
          } catch (_) {}
          navigate(to);
        }}
      >
        {name}
      </span>
      {after}
    </>
  );
}

export function formatNotificationTime(createdAt) {
  if (!createdAt) return "";
  try {
    const d = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

function notificationAvatarReferrerPolicy(src) {
  if (!src) return undefined;
  try {
    const u = new URL(src, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const h = u.hostname.toLowerCase();
    if (
      h.includes("googleusercontent") ||
      h.includes("ggpht.com") ||
      h.includes("maps.googleapis.com") ||
      h.includes("amazonaws.com") ||
      h.includes("cloudfront.net") ||
      h === "lh3.googleusercontent.com"
    ) {
      return "no-referrer";
    }
  } catch (_) {}
  return undefined;
}

function normalizeNotificationMediaUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  if (s.startsWith("/")) {
    try {
      return `${String(getApiBaseUrl()).replace(/\/$/, "")}${s}`;
    } catch {
      return s;
    }
  }
  return s;
}

/** Collect image candidates from flat notification + persisted `meta` blob. */
function notificationImageCandidates(n) {
  const meta = n?.meta && typeof n.meta === "object" && !Array.isArray(n.meta) ? n.meta : {};
  return [
    n?.imageUrl,
    n?.providerImage,
    n?.image,
    n?.avatarUrl,
    n?.senderAvatar,
    n?.thumbnailUrl,
    n?.patientAvatar,
    meta.imageUrl,
    meta.providerImage,
    meta.image,
    meta.avatarUrl,
    meta.senderAvatar,
    meta.thumbnailUrl,
  ];
}

/** Resolve doctor/hospital (or other) portrait URLs — https, `//`, or `/uploads/...` via API origin. */
export function resolvePatientNotificationAvatarSrc(n) {
  for (const c of notificationImageCandidates(n)) {
    const resolved = normalizeNotificationMediaUrl(c);
    if (resolved) return resolved;
  }
  return "";
}

/** Larger preview when API sends a distinct thumbnail (not already used as avatar). */
export function resolvePatientNotificationPreviewSrc(n) {
  const meta = n?.meta && typeof n.meta === "object" && !Array.isArray(n.meta) ? n.meta : {};
  const candidates = [
    n?.previewImageUrl,
    n?.attachmentUrl,
    n?.bannerUrl,
    meta.previewImageUrl,
    meta.attachmentUrl,
    meta.bannerUrl,
    meta.thumbnailUrl,
    n?.thumbnailUrl,
  ];
  for (const c of candidates) {
    const resolved = normalizeNotificationMediaUrl(c);
    if (resolved) return resolved;
  }
  return "";
}

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase().slice(0, 2);
  }
  return s.slice(0, 2).toUpperCase();
}

/** Prefer structured fields; avoid using bare "New message" as initials source (→ "NM"). */
function messageInitialsSourceTitle(title) {
  const t = String(title || "").trim();
  const split = /^new message\s*[·•]\s*(.+)$/i.exec(t);
  if (split) return String(split[1] || "").trim();
  if (/^new message$/i.test(t)) return "";
  return t;
}

function displayNameForNotificationInitials(n, isMessage) {
  const pn = String(n?.providerName || "").trim();
  if (pn) return pn;
  const dn = String(n?.doctorName || n?.hospitalName || "").trim();
  if (dn) return dn;
  const title = String(n?.title || "").trim();
  if (isMessage) return messageInitialsSourceTitle(title);
  return title;
}

function fallbackRoleLabelForMessageAvatar(n) {
  const role = String(n?.providerRole || "").toLowerCase();
  if (role === "doctor") return "Doctor";
  if (role === "hospital") return "Hospital";
  if (role === "admin") return "Admin";
  return "";
}

function initialsLabelForPatientNotification(n, isMessage) {
  let label = displayNameForNotificationInitials(n, isMessage);
  if (!label && isMessage) label = fallbackRoleLabelForMessageAvatar(n);
  return label;
}

/** Standard patient upload confirmation from API — title already conveys meaning on the feed page. */
function isGenericPatientUploadBody(type, body) {
  const t = String(type || "").toLowerCase();
  if (t !== "prescription_upload_patient") return false;
  const b = String(body || "").trim();
  return /^We received \d+ file\(s\)\.\s*They will be reviewed shortly\.?$/i.test(b);
}

function pickNotificationIcon(type) {
  const t = String(type || "").toLowerCase();
  if (t === "chat_message" || t === "admin_message") return MessageSquare;
  if (t === "reverse_bid") return RefreshCw;
  if (t === "provider_accepted_bid" || t === "offer_accepted") return CheckCircle2;
  if (t === "bid_rejected") return XCircle;
  if (t === "appointment_confirmed") return Calendar;
  if (t === "prescription_rejected") return FileText;
  if (t === "prescription_accepted" || t === "prescription_bulk_accepted") return CheckCircle2;
  if (t === "prescription_upload_patient") return Upload;
  if (t === "bidding_opened") return Sparkles;
  if (t.includes("hospital")) return Stethoscope;
  return Bell;
}

/**
 * Shared row UI for patient notification bell + `/patient-notifications` page.
 * @param {{ notification: Record<string, unknown>, unread?: boolean, onRowActivate: () => void, onProviderNavigate?: () => void, compactCopy?: boolean }} props
 */
export function PatientNotificationRow({ notification: n, unread, onRowActivate, onProviderNavigate, compactCopy = false }) {
  const type = String(n?.type || "").toLowerCase();
  const isMessage = type === "chat_message" || type === "admin_message";
  const explicitPath = getPatientNotificationPath(n);
  const hasWebDestination = Boolean(explicitPath);
  const messageDeepLink = Boolean(
    explicitPath && isMessage && String(explicitPath).includes("patient-chats")
  );
  const avatarSrc = resolvePatientNotificationAvatarSrc(n);
  const previewSrcRaw = resolvePatientNotificationPreviewSrc(n);
  const previewSrc =
    previewSrcRaw && previewSrcRaw !== avatarSrc ? previewSrcRaw : "";
  const baseLeadAvatarMode =
    type === "reverse_bid" ||
    isMessage ||
    type === "provider_accepted_bid" ||
    type === "bid_rejected" ||
    type === "offer_accepted" ||
    type === "appointment_confirmed";
  /** Show circular photo whenever API/socket supplied an image (stored meta included). */
  const leadAvatarMode = Boolean(avatarSrc) || baseLeadAvatarMode;
  const Icon = pickNotificationIcon(n.type);
  const nameForInitials = initialsLabelForPatientNotification(n, isMessage);
  const [avatarImgFailed, setAvatarImgFailed] = useState(false);

  useEffect(() => {
    setAvatarImgFailed(false);
  }, [avatarSrc]);

  const hideBody = Boolean(compactCopy && isGenericPatientUploadBody(n.type, n.body));

  const rowClass = [
    "patient_notification_item",
    unread ? "patient_notification_item--unread" : "",
    "patient_notification_item--navigable",
    messageDeepLink ? "patient_notification_item--message" : "",
    hasWebDestination ? "patient_notification_item--linked" : "",
    hideBody ? "patient_notification_item--compactBody" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const ariaLabel = [String(n?.title || n?.type || "Notification").trim(), String(n?.body || "").trim().slice(0, 120)]
    .filter(Boolean)
    .join(". ");

  return (
    <li className={rowClass}>
      <button
        type="button"
        className="patient_notification_item__activator"
        onClick={onRowActivate}
        aria-label={ariaLabel}
      >
        <div className="patient_notification_item__inner">
          <div
            className={`patient_notification_item__media${
              leadAvatarMode ? " patient_notification_item__media--avatar" : ""
            }`}
          >
            {leadAvatarMode ? (
              <>
                {avatarSrc && !avatarImgFailed ? (
                  <img
                    className="patient_notification_item__avatarImg"
                    src={avatarSrc}
                    alt=""
                    referrerPolicy={notificationAvatarReferrerPolicy(avatarSrc)}
                    loading="lazy"
                    decoding="async"
                    onError={() => setAvatarImgFailed(true)}
                  />
                ) : (
                  <span className="patient_notification_item__avatarInitials" aria-hidden>
                    {initialsFromName(nameForInitials)}
                  </span>
                )}
                <span className="patient_notification_item__typeBadge" aria-hidden>
                  <Icon size={12} strokeWidth={2.4} />
                </span>
              </>
            ) : (
              <span className="patient_notification_item__iconRing" aria-hidden>
                <Icon size={18} strokeWidth={2.2} />
              </span>
            )}
          </div>
          <div className="patient_notification_item__main">
            <div className="patient_notification_item_title">{n.title || n.type}</div>
            {!hideBody ? (
              <div className="patient_notification_item_body">
                <PatientNotificationBody notification={n} onProviderNavigate={onProviderNavigate} />
              </div>
            ) : null}
            {previewSrc ? (
              <div className="patient_notification_item__preview">
                <img
                  src={previewSrc}
                  alt=""
                  className="patient_notification_item__previewImg"
                  referrerPolicy={notificationAvatarReferrerPolicy(previewSrc)}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
            <div className="patient_notification_item__footer">
              {(n.updatedAt || n.createdAt) && (
                <div className="patient_notification_item_time">
                  {formatNotificationTime(n.updatedAt || n.createdAt)}
                </div>
              )}
              {messageDeepLink ? (
                <span className="patient_notification_item__cta">Open chat</span>
              ) : hasWebDestination ? (
                <span className="patient_notification_item__cta">Open</span>
              ) : null}
            </div>
          </div>
          {hasWebDestination ? (
            <ChevronRight className="patient_notification_item__chevron" size={20} aria-hidden />
          ) : null}
        </div>
      </button>
    </li>
  );
}

/**
 * Same behavior as Admin Sidebar notification bell: badge, popup list, socket + polling fallback.
 * @param {{ variant?: "default" | "header" }} props — `header`: light icon for green navbar.
 */
export default function PatientNotificationBell({ variant = "default" }) {
  const navigate = useNavigate();
  const reduxToken = useSelector((s) => s.auth?.token);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState(() => {
    try {
      const s = localStorage.getItem(VIEWED_STORAGE_KEY);
      return s ? Number(s) : 0;
    } catch {
      return 0;
    }
  });
  const popupRef = useRef(null);

  const unreadCount = notifications.filter((n) => {
    const t = n.updatedAt || n.createdAt;
    if (!t) return false;
    const ts = new Date(t).getTime();
    if (isNaN(ts)) return false;
    return ts > lastViewedAt;
  }).length;

  const markNotificationsViewed = useCallback((notificationList) => {
    const now = Date.now();
    let viewedAt = now;
    if (Array.isArray(notificationList) && notificationList.length > 0) {
      const maxTs = notificationList.reduce((max, n) => {
        const t = n.updatedAt || n.createdAt;
        if (!t) return max;
        const ts = new Date(t).getTime();
        return isNaN(ts) ? max : Math.max(max, ts);
      }, 0);
      viewedAt = Math.max(now, maxTs + 1);
    }
    setLastViewedAt(viewedAt);
    try {
      localStorage.setItem(VIEWED_STORAGE_KEY, String(viewedAt));
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent("healbid_patient_notifications_viewed", { detail: viewedAt }));
    } catch (_) {}
  }, []);

  const fetchNotifications = useCallback(
    async (markAsViewedAfterFetch = false) => {
      if (!getPatientAccessToken(reduxToken)) {
        setNotifications([]);
        return;
      }
      setNotificationsLoading(true);
      try {
        const notifRes = await API.get(ENDPOINTS.PATIENT_NOTIFICATIONS, {
          params: { page: 1, limit: 50 },
        });
        const apiList = normalizeNotificationListFromResponse(notifRes);

        let rxItems = [];
        try {
          const rxRes = await API.get(ENDPOINTS.GET_MY_PRESCRIPTIONS);
          const rxBody = rxRes?.data ?? {};
          rxItems = Array.isArray(rxBody.items) ? rxBody.items : [];
        } catch (rxErr) {
          console.warn("Patient prescriptions merge skipped:", rxErr?.message || rxErr);
        }

        const merged = mergePrescriptionRejectedNotifications(apiList, rxItems);
        merged.sort((a, b) => {
          const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return tb - ta;
        });
        setNotifications(merged);
        if (markAsViewedAfterFetch) markNotificationsViewed(merged);
      } catch (err) {
        console.error("Patient fetch notifications error:", err);
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    },
    [markNotificationsViewed, reduxToken]
  );

  const openOrToggleNotifications = useCallback(() => {
    const next = !notificationOpen;
    if (next && !getPatientAccessToken(reduxToken)) {
      navigate("/get-started", { state: { from: "/care-price" } });
      return;
    }
    setNotificationOpen(next);
    if (next) fetchNotifications(true);
  }, [notificationOpen, reduxToken, fetchNotifications, navigate]);

  useEffect(() => {
    if (!notificationOpen) return;
    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !e.target.closest(".patient-ring-btn")
      ) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notificationOpen]);

  useEffect(() => {
    const onViewed = (e) => {
      try {
        const value = e?.detail != null ? Number(e.detail) : Number(localStorage.getItem(VIEWED_STORAGE_KEY));
        if (!isNaN(value)) setLastViewedAt(value);
      } catch (_) {}
    };
    window.addEventListener("healbid_patient_notifications_viewed", onViewed);
    return () => window.removeEventListener("healbid_patient_notifications_viewed", onViewed);
  }, []);

  useEffect(() => {
    if (!getPatientAccessToken(reduxToken)) return;
    fetchNotifications();
  }, [fetchNotifications, reduxToken]);

  /** Refetch when realtime toasts / me:refresh fire so the bell list stays in sync with prescriptions. */
  useEffect(() => {
    const onPatientSignal = () => {
      if (!getPatientAccessToken(reduxToken)) return;
      void fetchNotifications(false);
    };
    window.addEventListener("healbid:patient-notification", onPatientSignal);
    return () => window.removeEventListener("healbid:patient-notification", onPatientSignal);
  }, [fetchNotifications, reduxToken]);

  useEffect(() => {
    if (!getPatientAccessToken(reduxToken)) return;

    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        try {
          fetchNotifications();
        } catch (_) {}
      }, 10000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: "patient" });
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }

    const onNotification = (payload) => {
      try {
        fetchNotifications();
        // Do not showAlert here: PatientRealtimeNotifications already toasts `notification:new`
        // with shouldSuppressChatSocketToast (avoids duplicate + Bell bypassing care-price / visibility rules).
      } catch (_) {}
    };
    const onBidUpdate = () => {
      try {
        fetchNotifications();
      } catch (_) {}
    };
    socket.on("notification:new", onNotification);
    socket.on("bid:update", onBidUpdate);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();
    return () => {
      try {
        socket.off("notification:new", onNotification);
        socket.off("bid:update", onBidUpdate);
        socket.off("connect", stopPolling);
        socket.off("disconnect", startPolling);
        socket.off("connect_error", startPolling);
      } catch (_) {}
      stopPolling();
    };
  }, [fetchNotifications, reduxToken]);

  return (
    <div
      className={`patientNotifBellWrap ${
        variant === "header" ? "patientNotifBellWrap--onDark" : ""
      }`}
    >
      <div
        className="patient-ring-btn"
        onClick={openOrToggleNotifications}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openOrToggleNotifications();
          }
        }}
        aria-label="Notifications"
      >
        <span>
          <Bell className="patient-ring-btn__icon" size={22} strokeWidth={2.2} aria-hidden />
        </span>
        {unreadCount > 0 && (
          <span className="patient_notification_badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </div>

      {notificationOpen && (
        <div className="patient_notification_popup" ref={popupRef}>
          <div className="patient_notification_popup_header">
            <h3>Notifications</h3>
          </div>
          <div className="patient_notification_popup_body">
            {notificationsLoading ? (
              <ul className="patient_notification_list" aria-label="Loading notifications">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <li key={`patient-notif-skel-${idx}`} className="patient_notification_item">
                    <div className="patient_notification_item__inner">
                      <div className="patient_notification_item__media">
                        <SkeletonBlock height={44} width={44} className="patient_notification_item__skelRing" />
                      </div>
                      <div className="patient_notification_item__main">
                        <div className="patient_notification_item_title">
                          <SkeletonBlock height={14} width="60%" />
                        </div>
                        <div className="patient_notification_item_body" style={{ marginTop: 8 }}>
                          <SkeletonText lines={2} lineHeight={12} gap={8} />
                        </div>
                        <div className="patient_notification_item__footer" style={{ marginTop: 8 }}>
                          <SkeletonBlock height={10} width="35%" />
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : notifications.length === 0 ? (
              <div className="patient_notification_empty">No notifications</div>
            ) : (
              <ul className="patient_notification_list">
                {notifications.map((n, i) => (
                  <PatientNotificationRow
                    key={`${n.type}-${n.messageId || n.bidLogId || n.appointmentId || n.prescriptionId || n.matchRequestId || ""}-${i}`}
                    notification={n}
                    unread={isNotificationUnread(n, lastViewedAt)}
                    onRowActivate={() => {
                      const path = getPatientNotificationPath(n) || "/HealBidDashboard";
                      setNotificationOpen(false);
                      navigate(path);
                    }}
                    onProviderNavigate={() => setNotificationOpen(false)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
